/** @format */

import { createFileRoute } from "@tanstack/react-router";
import { Suspense } from "react";

import { lazyWithRetry } from "@/utils/lazyWithRetry";
import PageLoader from "@/utils/PageLoader";
import { RouteErrorComponent } from "@/utils/RouteErrorComponent";
import {
  fetchRacesBySeriesAndYear,
  fetchStandingsBySeriesAndYear,
  yearSearchSchema,
} from "@/utils/routeUtils";

const SeriesStandingsPage = lazyWithRetry(
  () => import("@/pages/Series/SeriesStandings")
);

export const Route = createFileRoute("/series/$series/standings")({
  validateSearch: yearSearchSchema,
  loaderDeps: ({ search }) => ({ year: search.year }),
  /**
   * Loader prefetches data for instant rendering
   *
   * NOTE: The component also uses useSeasonStandings() and useRacesList() hooks.
   * This is INTENTIONAL and follows the recommended TanStack Router + React Query pattern:
   *
   * - Loader prefetch = Instant render (no loading spinner)
   * - Component hooks = Reactive updates + type-safe data access
   *
   * React Query's cache deduplication prevents duplicate network requests.
   * Both fetch functions use query options factories internally (single source of truth).
   */
  loader: async ({ context, params, deps }) => {
    const { queryClient } = context;
    const { series } = params;
    const year = String(deps.year ?? new Date().getFullYear());

    // Prefetch standings and schedule in parallel using shared query options factories
    await Promise.all([
      fetchStandingsBySeriesAndYear(queryClient, series, year),
      fetchRacesBySeriesAndYear(queryClient, series, year),
    ]);
  },
  component: () => (
    <Suspense fallback={<PageLoader />}>
      <SeriesStandingsPage />
    </Suspense>
  ),
  errorComponent: RouteErrorComponent,
});
